import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PieImage } from 'components/common'
import { ContentModal } from 'components/organisms/modals/content-modal'
import SocialShareBlock from 'components/organisms/social-share-block'

interface EventShareSuccessModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	event?: {
		displayName?: string
		avatarUrl?: string
		slug?: string
		isPrivate?: boolean
	}
	clubId?: string
	referralCode?: string | null
}

export function EventShareSuccessModal({ open, setOpen, event, clubId, referralCode }: EventShareSuccessModalProps) {
	const navigate = useNavigate()

	const handleClose = () => {
		setOpen(false)
		navigate(
			clubId ? `/manage/rep-clubs/${clubId}/events/${event?.slug}` : `/manage/rep-clubs/events/${event?.slug}`,
			{ replace: true }
		)
	}

	return (
		<ContentModal open={open} setOpen={setOpen} isModal onClickClose={handleClose}>
			<div className="relative flex flex-col items-center space-y-5">
				<div className="max-w-[304px]">
					<div>
						<h3 className="text-2xl font-medium mb-5 flex justify-center items-center gap-2">
							Congratulations!
						</h3>
						<div>
							You have successfully created the <strong>{event?.displayName}</strong> Event.
						</div>
					</div>
					<div className="flex justify-center items-center py-8">
						<PieImage
							className="w-[128px] h-auto rounded-xl overflow-hidden flex-shrink-0"
							images={[event?.avatarUrl]}
						/>
					</div>
					{!event?.isPrivate ? (
						<div>
							<div className="text-primary-text">Share with your community</div>
							<div className="mt-3 mb-6">
								<SocialShareBlock
									className="!justify-center"
									url={`${import.meta.env.VITE_SHARE_URL}/e/${event?.slug}${referralCode ? `?ref=${referralCode}` : ''}`}
								/>
							</div>
						</div>
					) : (
						<div>
							<div className="text-primary-text">Share with your community</div>
							<div className="text-sm">
								Private events cannot be shared. You can only invite attendees or share private tickets.
							</div>
						</div>
					)}
				</div>
			</div>
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface EventShareSuccessModalManagedProps {
	event?: {
		displayName?: string
		avatarUrl?: string
		slug?: string
		isPrivate?: boolean
	}
	clubId?: string
	referralCode?: string | null
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'event-share-success': EventShareSuccessModalManagedProps
	}
}

export function ManagedEventShareSuccessModal({
	onClose,
	...rest
}: EventShareSuccessModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<EventShareSuccessModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			{...rest}
		/>
	)
}
